import { useState } from 'react';
import { toDateKey } from '@/lib/dateKey';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function breakLaneClass(lane: NonNullable<DayMark['breakLanes']>[number]): string {
  const edge = (connect: boolean, round: boolean, side: 'left' | 'right') =>
    connect
      ? `calendar-day__break--connect-${side}`
      : round
        ? `calendar-day__break--round-${side}`
        : `calendar-day__break--flush-${side}`;
  // School lanes are painted by their id class. Stay lanes carry their
  // color in the data, so they share the occupancy class (positioning)
  // plus a per-stay class, and get their background inline.
  const kind = lane.color ? 'occupancy' : lane.id;
  return [
    'calendar-day__break',
    `calendar-day__break--${kind}`,
    lane.color ? `calendar-day__break--stay-${lane.id}` : undefined,
    lane.raise ? `calendar-day__break--raise-${lane.raise}` : undefined,
    edge(lane.connectLeft, lane.roundLeft, 'left'),
    edge(lane.connectRight, lane.roundRight, 'right'),
  ]
    .filter(Boolean)
    .join(' ');
}

function breakLabelClass(lane: NonNullable<DayMark['breakLanes']>[number]): string {
  return [
    'calendar-day__break-label',
    lane.raise ? `calendar-day__break-label--raise-${lane.raise}` : undefined,
  ]
    .filter(Boolean)
    .join(' ');
}

export interface DayMark {
  /** Extra class appended to the day button, e.g. for a background tint. */
  className?: string;
  /** Short (<=6 char) text rendered under the day number, e.g. "XMAS". */
  caption?: string;
  /** Full human-readable text folded into the day's aria-label (and
   * available to the caller for a fuller "selected day" display) — e.g.
   * "Christmas Day" rather than the on-cell "XMAS". */
  label?: string;
  /** Saved-flight mark ("FLT", or a count when several). Rendered apart
   * from `caption` so a Chip/holiday label stays on the cell. */
  flightMark?: string;
  /** Per-school break bands, then stay bands. Lane order is the array order. */
  breakLanes?: ReadonlyArray<{
    /** 'barnstable' | 'skidmore' for school bands, the stay id for stay bands. */
    id: string;
    /** Stay bands only: the segment color from the stay data. School
     * bands leave this unset and are painted by their id class. */
    color?: string;
    /** Stay bands only: who is there (the segment label). Printed
     * inside the bar where `labelStart` is set. */
    label?: string;
    /** Stay bands only: a visible run of this segment starts on this
     * cell in this week row, so the in-bar label prints here. */
    labelStart?: boolean;
    /** Stay bands only: days from this cell to the end of this
     * segment's run in this row — the label's width cap. */
    labelRunDays?: number;
    /** Stay bands only: readable text color on the segment color. */
    labelColor?: string;
    connectLeft: boolean;
    connectRight: boolean;
    roundLeft: boolean;
    roundRight: boolean;
    /** Slots above the bottom band so a stay does not cover a school band. */
    raise?: number;
  }>;
}

interface CalendarProps {
  selectedDate: string | null;
  onSelect: (dateKey: string) => void;
  minDate?: Date;
  /** Optional per-day decoration (highlight color, holiday marker, ...). */
  markDay?: (dateKey: string, date: Date) => DayMark | undefined;
}

export function Calendar({ selectedDate, onSelect, minDate, markDay }: CalendarProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const floor = minDate ?? today;
  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array(startWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i + 1)),
  ];

  const monthLabel = viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          aria-label="Previous month"
        >
          ‹
        </button>
        <strong style={{ fontSize: 16 }}>{monthLabel}</strong>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="calendar-grid" role="row">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="calendar-weekday" aria-hidden>
            {w}
          </div>
        ))}
      </div>
      <div className="calendar-weeks" role="grid" aria-label="Departure date">
        {Array.from({ length: Math.ceil(cells.length / 7) }, (_, wi) => {
          const week = cells.slice(wi * 7, wi * 7 + 7);
          const marks = week.map((date) => (date ? markDay?.(toDateKey(date), date) : undefined));
          const maxLanes = Math.max(0, ...marks.map((mark) => mark?.breakLanes?.length ?? 0));
          const hasBreak = maxLanes > 0;
          return (
            <div
              key={wi}
              className={`calendar-week${hasBreak ? ' calendar-week--breaks' : ''}${maxLanes >= 3 ? ' calendar-week--three-bands' : ''}`}
              role="row"
            >
              {week.map((date, i) => {
                if (!date) return <div key={i} />;
                const key = toDateKey(date);
                const disabled = date < floor;
                const isToday = toDateKey(today) === key;
                const isSelected = selectedDate === key;
                const mark = marks[i];
                const label = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                return (
                  <button
                    key={i}
                    type="button"
                    className={`calendar-day${isToday ? ' is-today' : ''}${isSelected ? ' is-selected' : ''}${mark?.flightMark ? ' has-saved-flight' : ''}${mark?.className ? ` ${mark.className}` : ''}`}
                    disabled={disabled}
                    onClick={() => onSelect(key)}
                    aria-label={mark?.label ? `${label} — ${mark.label}` : label}
                    aria-current={isToday ? 'date' : undefined}
                    aria-pressed={isSelected}
                  >
                    <span className="calendar-day__number">{date.getDate()}</span>
                    {mark?.flightMark && (
                      <span className="calendar-day__flight" aria-hidden>
                        {mark.flightMark}
                      </span>
                    )}
                    {mark?.caption && (
                      <span className="calendar-day__caption" aria-hidden>
                        {mark.caption}
                      </span>
                    )}
                    {mark?.breakLanes?.map((lane) => (
                      <span
                        key={lane.id}
                        className={breakLaneClass(lane)}
                        style={lane.color ? { background: lane.color } : undefined}
                        aria-hidden
                      />
                    ))}
                    {mark?.breakLanes?.map((lane) => {
                      // In-bar name for a stay band, printed where a run
                      // of the segment starts in this row. Rendered as a
                      // sibling of the band (not inside it) so it can
                      // span the run's cells, capped at the run's width
                      // — 100% is one cell, each extra day adds a cell
                      // plus the 6px grid gap — and ellipsized beyond it.
                      if (!lane.color || !lane.label || !lane.labelStart) return null;
                      const runDays = lane.labelRunDays ?? 1;
                      return (
                        <span
                          key={`${lane.id}-label`}
                          className={breakLabelClass(lane)}
                          style={{
                            color: lane.labelColor,
                            maxWidth: `calc(${runDays} * 100% + ${(runDays - 1) * 6}px - 4px)`,
                          }}
                          aria-hidden
                        >
                          {lane.label}
                        </span>
                      );
                    })}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
